import {
  useGetDashboard,
  useSetHomeDashboard,
  useDuplicateDashboard,
  getListDashboardsQueryKey,
} from '@smarthome/api';
import { useSettingsStore } from '@smarthome/store';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  AlertCircle,
  Home,
  LayoutDashboard,
  Pencil,
  MoreVertical,
  Share2,
  Lock,
  Download,
  Copy,
  History,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

import { Badge, Dropdown, IconButton, useToast } from '@/components/ui';
import { ExportSingleModal, HistoryModal } from '@/features/dashboards/modals';
import { getWidgetDefinition } from '@/features/dashboards/registry';
import { DashboardViewer } from '@/features/dashboards/viewer';
import type { DashboardTimeRange } from '@/features/dashboards/viewer/DashboardTimeRangeContext';
import { DEFAULT_DASHBOARD_TIME_RANGE } from '@/features/dashboards/viewer/DashboardTimeRangeContext';
import { TimeRangeSelector } from '@/features/dashboards/viewer/TimeRangeSelector';

export function DashboardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { sidebarHidden } = useSettingsStore();

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [timeRange, setTimeRange] = useState<DashboardTimeRange>(DEFAULT_DASHBOARD_TIME_RANGE);

  const { data, isLoading, isError, refetch } = useGetDashboard(id ?? '', {
    query: {
      enabled: !!id,
    },
  });

  const dashboard = data?.dashboard;

  // Initialize time range from document when data first loads
  const initializedRef = useRef(false);
  useEffect(() => {
    if (dashboard?.document?.timeRange && !initializedRef.current) {
      setTimeRange(dashboard.document.timeRange as DashboardTimeRange);
      initializedRef.current = true;
    }
  }, [dashboard?.document?.timeRange]);

  // Show time range selector when at least one widget uses dashboard time range
  const showTimeRangeSelector = useMemo(() => {
    const widgets = dashboard?.document?.widgets ?? [];
    return widgets.some((w) => {
      const def = w.type
        ? getWidgetDefinition(w.type as Parameters<typeof getWidgetDefinition>[0])
        : undefined;
      if (!def?.supportsTimeRange) return false;
      const config = w.config as { timeRangeMode?: string } | null | undefined;
      return (config?.timeRangeMode ?? 'fixed') === 'dashboard';
    });
  }, [dashboard?.document?.widgets]);

  const { mutate: setHomeDashboard } = useSetHomeDashboard({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Home dashboard updated', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListDashboardsQueryKey() });
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to set home dashboard',
          variant: 'error',
        });
      },
    },
  });

  const { mutate: duplicateDashboard } = useDuplicateDashboard({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Dashboard duplicated', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListDashboardsQueryKey() });
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to duplicate dashboard',
          variant: 'error',
        });
      },
    },
  });

  const handleSetHome = () => {
    if (id) {
      setHomeDashboard({ data: { dashboardId: id } });
    }
  };

  const handleDuplicate = () => {
    if (id) {
      duplicateDashboard({ id });
    }
  };

  const handleEdit = () => {
    if (id) {
      navigate(ROUTES.DASHBOARD_EDIT.replace(':id', id));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (isError || !dashboard) {
    return (
      <div className={sidebarHidden ? '' : 'space-y-6'}>
        {!sidebarHidden && (
          <div>
            <Link
              to={ROUTES.DASHBOARDS}
              className="inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-900 dark:text-zinc-500 dark:hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to dashboards
            </Link>
          </div>
        )}
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
            <AlertCircle className="h-6 w-6 text-red-400" />
          </div>
          <p className="text-sm text-gray-600 dark:text-zinc-400">Failed to load dashboard</p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm text-blue-500 hover:text-blue-400"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const menuItems = [
    ...(dashboard.isEditable
      ? [
          {
            value: 'edit',
            label: 'Edit dashboard',
            icon: <Pencil className="h-4 w-4" />,
          },
        ]
      : []),
    {
      value: 'set-home',
      label: 'Set as home',
      icon: <Home className="h-4 w-4" />,
    },
    {
      value: 'duplicate',
      label: 'Duplicate',
      icon: <Copy className="h-4 w-4" />,
    },
    {
      value: 'export',
      label: 'Export',
      icon: <Download className="h-4 w-4" />,
    },
    ...(dashboard.isEditable
      ? [
          {
            value: 'history',
            label: 'History',
            icon: <History className="h-4 w-4" />,
          },
        ]
      : []),
  ];

  const handleMenuSelect = (value: string) => {
    switch (value) {
      case 'edit':
        handleEdit();
        break;
      case 'set-home':
        handleSetHome();
        break;
      case 'duplicate':
        handleDuplicate();
        break;
      case 'export':
        setIsExportModalOpen(true);
        break;
      case 'history':
        setIsHistoryModalOpen(true);
        break;
    }
  };

  return (
    <div className={sidebarHidden ? '' : 'space-y-6'}>
      {/* Header */}
      {!sidebarHidden && (
        <div className="flex items-start justify-between">
          <div>
            <Link
              to={ROUTES.DASHBOARDS}
              className="mb-3 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-900 dark:text-zinc-500 dark:hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to dashboards
            </Link>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
                <LayoutDashboard className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                    {dashboard.name}
                  </h1>
                  {dashboard.isShared && (
                    <Badge variant="default" size="sm">
                      {dashboard.isEditable ? (
                        <Share2 className="mr-1 h-3 w-3" />
                      ) : (
                        <Lock className="mr-1 h-3 w-3" />
                      )}
                      {dashboard.isEditable ? 'Shared' : 'View only'}
                    </Badge>
                  )}
                </div>
                {dashboard.description && (
                  <p className="mt-0.5 text-sm text-gray-500 dark:text-zinc-500">
                    {dashboard.description}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {showTimeRangeSelector && (
              <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            )}
            <Dropdown
              trigger={
                <IconButton icon={<MoreVertical className="h-4 w-4" />} label="More actions" />
              }
              align="right"
              onSelect={handleMenuSelect}
              items={menuItems}
            />
          </div>
        </div>
      )}

      {/* Time range selector for hidden sidebar mode */}
      {sidebarHidden && showTimeRangeSelector && (
        <div className="flex justify-end pb-2">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>
      )}

      {/* Dashboard Content */}
      <DashboardViewer document={dashboard.document} timeRange={timeRange} />

      {/* Export Modal */}
      {id && dashboard.name && (
        <ExportSingleModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          dashboardId={id}
          dashboardName={dashboard.name}
        />
      )}

      {/* History Modal */}
      {id && dashboard.name && dashboard.isEditable && (
        <HistoryModal
          isOpen={isHistoryModalOpen}
          onClose={() => setIsHistoryModalOpen(false)}
          dashboardId={id}
          dashboardName={dashboard.name}
        />
      )}
    </div>
  );
}
